/** Hausnummern: aus der Straßenzeile lösen, vergleichen, gegen DHL-Bereiche prüfen */
import type { Address } from "../order.js";

export interface HouseNumber {
  /** numerischer Teil, z. B. 12 bei "12a" */
  number: number;
  /** Zusatz in Kleinbuchstaben, z. B. "a"; leer wenn keiner */
  suffix: string;
  /** Originalschreibweise, z. B. "12 a" oder "12-14" */
  raw: string;
}

export interface SplitStreet {
  street: string;
  houseNumber: HouseNumber | null;
  /** Rest nach der Hausnummer (z. B. "Hinterhaus", "2. OG") – gehört in den Adresszusatz */
  rest: string;
}

const HNR = String.raw`(\d{1,4})\s?([a-zA-Z](?![a-zA-Z]))?(?:\s?[-/]\s?\d{1,4}\s?[a-zA-Z]?)?`;

export function parseHouseNumber(s: string): HouseNumber | null {
  const m = new RegExp(`^\\s*${HNR}\\s*$`).exec(s);
  if (!m) return null;
  return { number: Number(m[1]), suffix: (m[2] ?? "").toLowerCase(), raw: s.trim() };
}

/**
 * Straßenzeile zerlegen: "Hauptstr. 12a, Hinterhaus" → Hauptstr. | 12a | Hinterhaus.
 * Unterstützt auch "Hauptstr.12", "12 Hauptstraße" und ein separates Hausnummernfeld.
 */
export function splitStreet(street: string, houseNumberField?: string): SplitStreet {
  const s = street.trim().replace(/\s+/g, " ");
  if (houseNumberField?.trim()) {
    const own = parseHouseNumber(houseNumberField);
    if (own) return { street: s, houseNumber: own, rest: "" };
  }
  // Nummer nach dem Namen; Straßennamen mit Ziffern ("Straße 17", "Am 1. Mai") bleiben erhalten, weil die letzte Zahl zählt
  const tail = new RegExp(`^(.*?[a-zA-ZäöüÄÖÜß.)])\\s*(${HNR})\\s*(?:[,;]\\s*(.*)|\\s+(.*))?$`).exec(s);
  if (tail && tail[1]!.trim().length >= 2) {
    const hn = parseHouseNumber(tail[2]!);
    if (hn) return { street: tail[1]!.trim(), houseNumber: hn, rest: (tail[5] ?? tail[6] ?? "").trim() };
  }
  const lead = new RegExp(`^(${HNR})\\s+(.+)$`).exec(s);
  if (lead) {
    const hn = parseHouseNumber(lead[1]!);
    if (hn) return { street: lead[4]!.trim(), houseNumber: hn, rest: "" };
  }
  return { street: s, houseNumber: null, rest: "" };
}

/** DHL-Feld HNR-8: 4 Ziffern (rechtsbündig) + 4 Zeichen Zusatz, z. B. " 239    " oder "  12A   " */
export function parseHnr8(field: string): { number: number; suffix: string } | null {
  if (!field.trim()) return null;
  const n = Number(field.slice(0, 4).trim());
  if (!Number.isFinite(n)) return null;
  return { number: n, suffix: field.slice(4).trim().toLowerCase() };
}

const cmp = (a: { number: number; suffix: string }, b: { number: number; suffix: string }) => a.number - b.number || (a.suffix < b.suffix ? -1 : a.suffix > b.suffix ? 1 : 0);

/** Abschnitt einer Straße: Hausnummern von–bis (ohne Tausender), gerade/ungerade/alle, Tausenderstelle */
export interface StreetSection {
  from: { number: number; suffix: string } | null;
  to: { number: number; suffix: string } | null;
  /** G = nur gerade, U = nur ungerade, N = ungeteilt */
  parity: "G" | "U" | "N";
  thousand: number;
  code: string;
}

export function sectionContains(sec: StreetSection, hn: HouseNumber): boolean {
  if (Math.floor(hn.number / 1000) !== sec.thousand) return false;
  const rest = { number: hn.number % 1000, suffix: hn.suffix };
  if (sec.parity === "G" && rest.number % 2 !== 0) return false;
  if (sec.parity === "U" && rest.number % 2 !== 1) return false;
  if (sec.from && cmp(rest, sec.from) < 0) return false;
  // "bis 12" schließt "12a" ein (Zusatz-Nummern gehören zum Bereich der Hauptnummer)
  if (sec.to && cmp({ number: rest.number, suffix: "" }, { number: sec.to.number, suffix: "" }) > 0) return false;
  return true;
}

/** Leitcode (Frachtpost): PLZ(5) + Straßencode(3) + Hausnummer(3) + Prüfziffer (Gewichtung 4/9, Modulo 10) */
export function leitcode(plz: string, code: string, houseNumber: number): string {
  const base = `${plz}${code.padStart(3, "0")}${String(houseNumber % 1000).padStart(3, "0")}`;
  let sum = 0;
  for (let i = 0; i < base.length; i++) sum += Number(base[i]) * (i % 2 === 0 ? 4 : 9);
  return `${base}${(10 - (sum % 10)) % 10}`;
}

/** Länder, in denen die Hausnummer hinter dem Straßennamen steht – nur dort ist eine Zahl in Zeile 2 sicher die Hausnummer */
export const HOUSE_NUMBER_AFTER_STREET = new Set(["DE", "AT", "CH", "NL", "BE", "LU"]);

// Hausnummer am Anfang von Adresszeile 2: "10", "10a", "10 a", "10-12", "Nr. 10" – danach Ende oder Trennzeichen.
// Ein Bereich zählt nur, wenn danach kein Punkt folgt ("10 - 2. OG" ist Nummer 10 + Etage, kein Bereich 10–2).
const ADDITION_HNR = /^(?:nr\.?\s*)?(\d{1,4}(?:\s?[a-zA-Z](?![a-zA-Z.]))?(?:\s?[-/]\s?\d{1,4}(?:[a-zA-Z](?![a-zA-Z]))?(?![\d.]))?)(?=$|[\s,;:–-])\s*[,;:–-]?\s*(.*)$/i;

/**
 * Grundbereinigung (für alle Kunden, ohne Leitdaten): Steht die Hausnummer nur in Adresszeile 2 und hat die Straße
 * keine Nummer, kommt sie in die Straße; Trennzeichen fallen weg, der Rest ("2. OG") bleibt Adresszusatz.
 * Gibt null zurück, wenn nichts zu tun ist oder es nicht eindeutig ist (Packstation, Postnummer, Straße mit Ziffern …).
 */
export function houseNumberFromAddition(a: Address): { address: Address; houseNumber: string; rest: string } | null {
  if (!HOUSE_NUMBER_AFTER_STREET.has((a.country ?? "").toUpperCase())) return null;
  if (a.houseNumber?.trim()) return null;
  const street = (a.street ?? "").trim().replace(/\s+/g, " ");
  const addition = (a.addition ?? "").trim().replace(/\s+/g, " ");
  if (!street || !addition) return null;
  // Straße mit Ziffern (Nummer schon drin, "Straße 17", "Am 1. Mai"): nicht anfassen
  if (/\d/.test(street)) return null;
  if (/packstation|postfiliale|filiale|postnummer|postfach/i.test(`${street} ${addition}`)) return null;
  const m = ADDITION_HNR.exec(addition);
  if (!m) return null;
  const houseNumber = m[1]!.replace(/\s+/g, " ").trim();
  const rest = (m[2] ?? "").replace(/^[,;:–-]\s*/, "").trim();
  const { addition: _old, ...base } = a;
  return { address: { ...base, street: `${street} ${houseNumber}`, ...(rest ? { addition: rest } : {}) }, houseNumber, rest };
}
